import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import Button from "@/components/ui/Button.jsx";
import Card from "@/components/ui/Card.jsx";
import DoctorResultCard from "@/components/doctor/DoctorResultCard.jsx";
import { resolveDoctorBookingEntryState } from "@/services/doctorBookingMode.js";
import { useLanguage } from "@/contexts/LanguageContext.jsx";

const ICON_NEXT = "/icons/doctor/appointments/navigate_next.svg";

function isFrenchLanguageCode(languageCode) {
  return String(languageCode || "").toLowerCase().startsWith("fr");
}

function readDoctorFromNavOrStorage(locationState, id) {
  const fromState = locationState?.doctor;
  if (fromState && String(fromState.id) === String(id)) return fromState;

  const keys = [`afrimed:doctor:${id}`, "afrimed:lastDoctor"];

  for (const key of keys) {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (parsed && String(parsed.id) === String(id)) return parsed;
    } catch {
      // ignore
    }
  }

  return null;
}

export default function AppointmentDoctorPreview() {
  const nav = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { t, languageCode } = useLanguage();
  const isFrench = isFrenchLanguageCode(languageCode);
  const [bookingPending, setBookingPending] = React.useState(false);

  const doctor = React.useMemo(
    () => readDoctorFromNavOrStorage(location.state, id),
    [location.state, id]
  );

  const openDoctorProfile = React.useCallback(() => {
    if (!doctor) return;
    nav("/doctor/profile", {
      state: {
        from: "patient",
        doctorUserId: doctor.id,
        doctor,
        returnTo: location.pathname,
      },
    });
  }, [doctor, location.pathname, nav]);

  const handleBookNow = React.useCallback(async () => {
    if (!doctor || bookingPending) return;
    setBookingPending(true);

    try {
      const resolved = await resolveDoctorBookingEntryState({
        doctorId: id,
        doctor,
        requestedMode: location.state?.mode || null,
        consultationType: location.state?.consultation_type || null,
        facilityId: location.state?.facilityId || null,
        facilityName: location.state?.facilityName || "",
        facility: location.state?.facility || null,
      });

      const bookingState = {
        doctor,
        mode: resolved.mode,
        facilityId: resolved.facilityId,
        facilityName: resolved.facilityName,
        facility: resolved.facility,
      };

      try {
        sessionStorage.setItem("afrimed:lastDoctor", JSON.stringify(doctor));
        sessionStorage.setItem(`afrimed:doctor:${doctor.id}`, JSON.stringify(doctor));
        sessionStorage.setItem(`afrimed:booking:${id}`, JSON.stringify({ doctor, ...bookingState }));
        sessionStorage.setItem("afrimed:booking:last", JSON.stringify({ doctor, ...bookingState }));
      } catch {
        // ignore
      }

      nav(`/appointments/book/${id}`, { state: bookingState });
    } finally {
      setBookingPending(false);
    }
  }, [bookingPending, doctor, id, location.state, nav]);

  return (
    <div className="min-h-[100dvh] bg-[rgb(var(--clr-surface-container-low))]">
      <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
        <TopBarCenter
          title={t("appointments.directory.preview.title", isFrench ? "Clinicien" : "Clinician")}
          close
          onBack={() => nav("/appointments/doctors")}
          right={
            doctor ? (
              <button
                type="button"
                onClick={openDoctorProfile}
                className="-mr-1 inline-flex h-10 items-center justify-end gap-1 pl-2 pr-0 text-body-medium text-on-surface-var"
              >
                <span>{t("appointments.directory.preview.profile", isFrench ? "Profil" : "Profile")}</span>
                <img src={ICON_NEXT} alt="" className="h-6 w-6 opacity-70" draggable="false" />
              </button>
            ) : null
          }
        />

        <div className="mt-4">
          {doctor ? (
            <DoctorResultCard
              doctor={doctor}
              showInsurance
              onLocate={(payload) => {
                const qs = new URLSearchParams();
                const facilityName = String(payload?.name || "").trim();
                if (facilityName) {
                  qs.set("facilityName", facilityName);
                }
                if (payload?.address) {
                  qs.set("facilityAddress", String(payload.address));
                }
                if (payload?.facilityId) {
                  qs.set("facilityId", String(payload.facilityId));
                }
                if (Number.isFinite(Number(payload?.lat))) {
                  qs.set("facilityLat", String(Number(payload.lat)));
                }
                if (Number.isFinite(Number(payload?.lng))) {
                  qs.set("facilityLng", String(Number(payload.lng)));
                }
                nav(`/appointments/doctors?${qs.toString()}`);
              }}
            />
          ) : (
            <Card className="px-4 py-4 text-body-medium text-on-surface-var">
              {t(
                "appointments.directory.preview.missing",
                isFrench
                  ? "Clinicien introuvable. Veuillez revenir en arrière et sélectionner à nouveau un clinicien."
                  : "Clinician not found. Please go back and select a clinician again."
              )}
            </Card>
          )}
        </div>

        <div className="mt-6 flex gap-3">
          <Button variant="ghost" className="flex-1 h-12" onClick={() => nav(-1)}>
            {t("appointments.directory.preview.back", isFrench ? "Retour" : "Back")}
          </Button>

          <Button
            className="flex-1 h-12"
            disabled={!doctor || bookingPending}
            onClick={handleBookNow}
          >
            {bookingPending
              ? t("appointments.directory.preview.loading", isFrench ? "Chargement..." : "Loading...")
              : t("appointments.directory.preview.bookNow", isFrench ? "Prendre rendez-vous" : "Book now")}
          </Button>
        </div>
      </div>
    </div>
  );
}
